// Query-key constants for the `growth/link-share` family (ARCFE-1062 · D).
//
// Frozen VERBATIM from their pre-move definition site,
// `src/features/session-sharing/hooks/useSharedSession.ts`. The values are the
// cache identity: `'getSharedSession'` and `'getSharedDocuments'` are what
// every existing `queryKey` tuple already writes and reads under, so changing
// either string would silently cold-start the receiver page's cache.
//
// They live HERE, not beside the hook, because `packages/CLAUDE.md` puts key
// strings in the `/contracts` key factory with the legacy site re-exporting
// them — exactly one declaration per identity. The old path is now a
// re-export shim, and `migration:check`'s `key-single-site` guard fails the
// build if either literal is ever re-declared under `src/**`.
export const GET_SHARED_SESSION = 'getSharedSession';
export const GET_SHARED_DOCUMENTS = 'getSharedDocuments';

// Specialty onboarding reads. Frozen verbatim from the hooks that declared
// them: each string is the cache identity those queries already write.
export const GET_SPECIALTY_ONBOARDING_SCRIPT = 'getSpecialtyOnboardingScript';
export const GET_SPECIALTY_ONBOARDING_V2_ASSETS = 'getSpecialtyOnboardingV2Assets';

/** User-scoped localStorage for meddevice compliance notification dismiss state (GRO-3989). */
export const COMPLIANCE_NOTIFICATION_DISMISS_STORAGE_KEY =
  'heidi.meddeviceComplianceNotificationDismiss';

// The engagement threshold the auto-open gate counts against, moved here with
// the hook that reads it. Not cache identities — product constants — but they
// land beside the dismiss key on the same rule: the app cone's `constants.ts`
// re-exports them rather than restating them, so each has ONE declaration.

/** Minimum scribe session length (seconds) to count toward engagement. */
export const QUALIFIED_SESSION_MIN_DURATION_SECONDS = 30;

/** Rolling window for qualified-session counting. */
export const QUALIFIED_SESSION_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

/** Engagement inflection point from product analysis (GRO-3989). */
export const REQUIRED_QUALIFIED_SESSIONS = 6;

// The two in-app destinations the pinned compliance cards recommend, moved here
// with the hook that maps a card type onto one.
//
// NOT cache identities and not routes this package navigates to — `/contracts`
// is inert and the package owns no router. Each is carried on a
// `PinnedComplianceNotification.settingsRoute` and handed back out through the
// card's `onOpenSettings` port, which the app's
// `useOpenComplianceSettings` binds to the settings modal. They land here for
// the same reason the dismiss key above did: ONE declaration per identity, with
// the old app path (`src/features/growth/meddevice-compliance-notifications/constants.ts`)
// a re-export shim rather than a second minting site.
//
// Both are the settings modal's own hash routes and are frozen the way a key is:
// `useSettingsModal` matches on the string and `useHashBasedScrolling` reads the
// hash, so an edit here silently deep-links a compliance nudge to the wrong
// screen — or to the right screen with no card highlighted — and nothing fails.

/**
 * The Login details account screen. Declared under the DESTINATION's name rather
 * than either reader's, because it has two readers with different errands: the
 * pinned MFA-setup recommendation below sends the user here to turn MFA on, and
 * `HealthNzMigrationModalContent` sends them here to change their email.
 *
 * That second reader is why this name exists at all. The literal was declared
 * twice app-side — once in this cone's `constants.ts` and once in that modal —
 * and `key-single-site` could not see it, because that check needs one side to be
 * a package `/contracts` file. Moving growth's copy in here made the pair
 * visible, and it grandfathers nothing at any age. One destination is one
 * identity, so the answer is one declaration both readers import; it is NOT the
 * "two resources, two identities" case that guard usually reports.
 */
export const LOGIN_DETAILS_SETTINGS_ROUTE = '/settings/account#login-details';

/**
 * Where the "Set up multi-factor authentication" recommendation sends the user.
 *
 * Bound to the name above, never to a second copy of the literal — this is the
 * frozen spelling the compliance cone's barrel publishes, and the alias is what
 * lets it keep that spelling without minting the string twice.
 */
export const MFA_SETUP_SETTINGS_ROUTE = LOGIN_DETAILS_SETTINGS_ROUTE;

/** Where the "Turn on inactivity timeout" recommendation sends the user. */
export const INACTIVITY_LOGOUT_SETTINGS_ROUTE = '/settings/account#security';

/** Session-scoped medication detection highlights (GRO-3934). */
export const MEDICATION_DETECTIONS_QUERY_KEY = 'medication_detections';

/**
 * Per-tab sessionStorage for the "Improve your notes" panel collapse choice,
 * moved here with its atom at the post-note-quality UI intake.
 *
 * Not a cache identity — a browser-storage key, registered in
 * `persistence-manifest.json` — but it lands in `/contracts` beside
 * `COMPLIANCE_NOTIFICATION_DISMISS_STORAGE_KEY` for the same reason that one
 * did: a persisted key is an identity, one identity gets ONE declaration, and
 * the old app path is now a re-export shim rather than a second minting site.
 * Changing the literal orphans every user's saved collapse state.
 */
export const IMPROVE_NOTES_PANEL_COLLAPSED_STORAGE_KEY =
  'heidi.postNoteQuality.improveNotesPanelCollapsed';
